import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MapPin, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { companyInfo, brands } from '../../data/mock';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  const whatsappLink = `https://wa.me/55${companyInfo.whatsapp}?text=Olá! Gostaria de um orçamento.`;

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">
      {/* Top Bar */}
      <div className="bg-[#2B3990] text-white py-2">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-between items-center text-sm">
            <div className="flex items-center gap-4 md:gap-6">
              <a href={`tel:${companyInfo.whatsapp}`} className="flex items-center gap-1 hover:text-[#8DC63F] transition-colors">
                <Phone size={14} />
                <span className="hidden sm:inline">{companyInfo.whatsappFormatted}</span>
              </a>
              <span className="flex items-center gap-1">
                <Clock size={14} />
                <span className="hidden md:inline">{companyInfo.hours}</span>
              </span>
            </div>
            <div className="flex items-center gap-1">
              <MapPin size={14} />
              <span className="hidden lg:inline">{companyInfo.fullAddress}</span>
              <span className="lg:hidden">Osasco - Centro</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={companyInfo.logo} 
              alt="Tsw Info - Assistência Técnica em Osasco" 
              className="h-12 md:h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6" translate="no">
            <Link 
              to="/" 
              className={`font-medium transition-colors hover:text-[#8DC63F] ${
                location.pathname === '/' ? 'text-[#8DC63F]' : 'text-[#2B3990]'
              }`}
            >
              Início
            </Link>

            <Link 
              to="/quem-somos" 
              className={`font-medium transition-colors hover:text-[#8DC63F] ${
                location.pathname === '/quem-somos' ? 'text-[#8DC63F]' : 'text-[#2B3990]'
              }`}
            >
              Quem Somos
            </Link>
            
            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <button className="font-medium text-[#2B3990] hover:text-[#8DC63F] transition-colors flex items-center gap-1">
                Serviços
                <svg className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isServicesOpen && (
                <div className="absolute top-full left-0 w-64 bg-white shadow-lg rounded-lg py-2 mt-1 border">
                  {brands.map((brand) => (
                    <Link
                      key={brand.id}
                      to={`/${brand.slug}`}
                      className="block px-4 py-2 text-gray-700 hover:bg-[#8DC63F]/10 hover:text-[#2B3990] transition-colors"
                    >
                      Assistência {brand.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#8DC63F] hover:bg-[#7ab635] text-white font-semibold px-6">
                <Phone size={18} className="mr-2" />
                Orçamento Grátis
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-[#2B3990]"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Menu"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t shadow-lg">
          <nav className="container mx-auto px-4 py-4" translate="no">
            <Link
              to="/"
              className="block py-3 text-[#2B3990] font-medium border-b"
              onClick={() => setIsMenuOpen(false)}
            >
              Início
            </Link>

            <Link
              to="/quem-somos"
              className="block py-3 text-[#2B3990] font-medium border-b"
              onClick={() => setIsMenuOpen(false)}
            >
              Quem Somos
            </Link>
            
            <div className="py-2">
              <p className="text-sm text-gray-500 uppercase tracking-wide py-2">Serviços</p>
              {brands.map((brand) => (
                <Link
                  key={brand.id}
                  to={`/${brand.slug}`}
                  className="block py-2 pl-4 text-gray-700 hover:text-[#8DC63F]"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {brand.name}
                </Link>
              ))}
            </div>

            <a 
              href={whatsappLink} 
              target="_blank" 
              rel="noopener noreferrer"
              className="block mt-4"
            >
              <Button className="w-full bg-[#8DC63F] hover:bg-[#7ab635] text-white font-semibold">
                <Phone size={18} className="mr-2" />
                Orçamento Grátis
              </Button>
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;